import { useMemo } from 'react';
import type { TrackingEvent } from '../../../utils/analytics';

interface DailyChartProps {
  events: TrackingEvent[];
}

const EVENT_COLORS: Record<string, string> = {
  page_view:      'bg-[#A5A5A5]',
  nav_click:      'bg-[#555555]',
  whatsapp_click: 'bg-green-500',
  phone_click:    'bg-[#7FCDCD]',
  preventivo_cta: 'bg-amber-400',
  form_submit:    'bg-orange-400',
};

const EVENT_LABELS: Record<string, string> = {
  page_view:      'Visite',
  nav_click:      'Navigazione',
  whatsapp_click: 'WhatsApp',
  phone_click:    'Telefono',
  preventivo_cta: 'CTA',
  form_submit:    'Form',
};

function getLast7Days(): string[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    return d.toISOString().slice(0, 10);
  });
}

function formatDay(date: string): string {
  const d = new Date(date + 'T00:00:00');
  return d.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric' });
}

export default function DailyChart({ events }: DailyChartProps) {
  const days = useMemo(() => getLast7Days(), []);

  const data = useMemo(() => {
    return days.map((date) => {
      const dayEvents = events.filter((e) => e.date === date);
      const byType: Record<string, number> = {};
      dayEvents.forEach((e) => {
        byType[e.type] = (byType[e.type] ?? 0) + 1;
      });
      return { date, total: dayEvents.length, byType };
    });
  }, [events, days]);

  const maxTotal = Math.max(...data.map((d) => d.total), 1);

  const eventTypes = ['page_view', 'nav_click', 'whatsapp_click', 'phone_click', 'preventivo_cta', 'form_submit'];

  return (
    <div className="bg-white rounded-2xl border border-[#E5E5E5] p-5 md:p-7">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h3 className="text-sm font-black text-[#000000]" style={{ fontFamily: "'Raleway', sans-serif" }}>
          Ultimi 7 giorni
        </h3>
        <div className="flex items-center gap-4 flex-wrap">
          {eventTypes.map((t) => (
            <div key={t} className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${EVENT_COLORS[t]}`} />
              <span className="text-xs text-[#A5A5A5]">{EVENT_LABELS[t]}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-end gap-2 h-40">
        {data.map((day) => (
          <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full flex flex-col justify-end" style={{ height: '120px' }}>
              <div
                className="w-full rounded-t-lg overflow-hidden flex flex-col-reverse"
                style={{ height: `${Math.round((day.total / maxTotal) * 100)}%`, minHeight: day.total > 0 ? '4px' : '0' }}
              >
                {eventTypes.map((t) => {
                  const count = day.byType[t] ?? 0;
                  if (!count) return null;
                  return (
                    <div
                      key={t}
                      title={`${EVENT_LABELS[t]}: ${count}`}
                      className={`w-full ${EVENT_COLORS[t]}`}
                      style={{ height: `${Math.round((count / day.total) * 100)}%`, minHeight: '3px' }}
                    />
                  );
                })}
              </div>
              {day.total === 0 && (
                <div className="w-full h-px bg-[#E5E5E5] rounded" />
              )}
            </div>
            <div className="text-center">
              <div className="text-[10px] font-black text-[#000000]">{day.total || ''}</div>
              <div className="text-[9px] text-[#A5A5A5] capitalize whitespace-nowrap">{formatDay(day.date)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
